/**
 * CookDayWizard — recipe-by-recipe kitchen view for the meal planner.
 *
 * Opens as a full-screen modal so parents can prop their phone/tablet next to
 * the stove and follow along without touching a mouse. Big steps, checkable
 * ingredients, prev/next buttons between recipes.
 */
import { useEffect, useState } from "react";
import {
  X,
  ChefHat,
  ChevronLeft,
  ChevronRight,
  Clock,
  Users,
  CheckCircle2,
  Circle,
  Sparkles,
} from "lucide-react";

export default function CookDayWizard({ open, onClose, scheduled = [], startIndex = 0 }) {
  const [idx, setIdx] = useState(startIndex);
  const [ingredientChecks, setIngredientChecks] = useState({});
  const [stepChecks, setStepChecks] = useState({});

  useEffect(() => {
    if (open) setIdx(startIndex);
  }, [open, startIndex]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight" && idx < scheduled.length - 1) setIdx(idx + 1);
      if (e.key === "ArrowLeft" && idx > 0) setIdx(idx - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, idx, scheduled.length, onClose]);

  if (!open || scheduled.length === 0) return null;

  const entry = scheduled[idx] || {};
  const recipe = entry.recipe || {};
  const ingredients = recipe.ingredients || [];
  const steps = recipe.steps || [];

  const toggleIng = (i) =>
    setIngredientChecks((s) => ({ ...s, [`${idx}-${i}`]: !s[`${idx}-${i}`] }));
  const toggleStep = (i) =>
    setStepChecks((s) => ({ ...s, [`${idx}-${i}`]: !s[`${idx}-${i}`] }));

  return (
    <div
      className="fixed inset-0 z-[100] bg-pb-ink/90 backdrop-blur-sm overflow-y-auto"
      data-testid="cook-day-wizard"
    >
      <div className="min-h-screen flex flex-col">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-pb-ink/95 backdrop-blur px-5 sm:px-8 py-4 flex items-center justify-between border-b border-white/10">
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-2xl bg-pb-coral flex items-center justify-center shadow-lg">
              <ChefHat className="h-5 w-5 text-white" />
            </span>
            <div>
              <p className="text-[10px] font-extrabold uppercase tracking-widest text-pb-sun">
                Cook day · Recipe {idx + 1} of {scheduled.length}
              </p>
              <p className="text-white font-display font-black text-lg leading-tight">
                {entry.day} — {entry.buddyName}
              </p>
            </div>
          </div>
          <button
            data-testid="btn-close-cook-day"
            onClick={onClose}
            className="rounded-full h-11 w-11 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white transition"
            aria-label="Close cook day"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Progress dots */}
        <div className="px-5 sm:px-8 py-4 flex flex-wrap items-center gap-2 justify-center">
          {scheduled.map((e, i) => (
            <button
              key={i}
              data-testid={`cook-day-dot-${i}`}
              onClick={() => setIdx(i)}
              className={`h-2.5 rounded-full transition-all ${
                i === idx
                  ? "w-10 bg-pb-coral"
                  : "w-2.5 bg-white/30 hover:bg-white/60"
              }`}
              aria-label={`Go to ${e.day}`}
            />
          ))}
        </div>

        {/* Recipe card */}
        <div className="flex-1 max-w-3xl w-full mx-auto px-5 sm:px-8 pb-24">
          <div className="bg-white rounded-[2rem] p-6 sm:p-10 shadow-[0_30px_80px_rgba(0,0,0,0.30)]">
            <span className="inline-flex items-center gap-2 rounded-full bg-pb-cream text-pb-coral font-extrabold text-xs px-3 py-1 uppercase tracking-widest">
              <Sparkles className="h-3 w-3" /> {entry.day}
            </span>
            <h1
              data-testid="cook-day-title"
              className="mt-4 font-display font-black text-3xl sm:text-4xl leading-tight text-pb-ink"
            >
              {recipe.title || entry.buddyName}
            </h1>
            {(recipe.time || recipe.serves) && (
              <div className="mt-3 flex flex-wrap gap-4 text-sm text-pb-soft font-bold">
                {recipe.time && (
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="h-4 w-4 text-pb-coral" /> {recipe.time}
                  </span>
                )}
                {recipe.serves && (
                  <span className="inline-flex items-center gap-1.5">
                    <Users className="h-4 w-4 text-pb-coral" /> {recipe.serves}
                  </span>
                )}
              </div>
            )}
            {recipe.story && (
              <p className="mt-4 italic text-pb-soft leading-relaxed">{recipe.story}</p>
            )}

            {/* Ingredients checklist */}
            {ingredients.length > 0 && (
              <section className="mt-8">
                <p className="text-xs font-extrabold uppercase tracking-widest text-pb-coral">
                  Ingredients
                </p>
                <ul className="mt-3 space-y-2">
                  {ingredients.map((ing, i) => {
                    const checked = !!ingredientChecks[`${idx}-${i}`];
                    return (
                      <li
                        key={i}
                        data-testid={`cook-day-ingredient-${i}`}
                        onClick={() => toggleIng(i)}
                        className={`flex items-start gap-3 p-3 rounded-xl cursor-pointer transition ${
                          checked ? "bg-pb-cream/60" : "hover:bg-pb-cream"
                        }`}
                      >
                        {checked ? (
                          <CheckCircle2 className="h-5 w-5 text-pb-green flex-shrink-0 mt-0.5" />
                        ) : (
                          <Circle className="h-5 w-5 text-pb-soft/50 flex-shrink-0 mt-0.5" />
                        )}
                        <span
                          className={`text-base ${
                            checked ? "line-through text-pb-soft" : "text-pb-ink"
                          }`}
                        >
                          {ing}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}

            {/* Steps — big, tap-friendly */}
            {steps.length > 0 && (
              <section className="mt-10">
                <p className="text-xs font-extrabold uppercase tracking-widest text-pb-coral">
                  Steps
                </p>
                <ol className="mt-4 space-y-4">
                  {steps.map((s, i) => {
                    const checked = !!stepChecks[`${idx}-${i}`];
                    return (
                      <li
                        key={i}
                        data-testid={`cook-day-step-${i}`}
                        onClick={() => toggleStep(i)}
                        className={`flex items-start gap-4 p-4 rounded-2xl cursor-pointer transition border-2 ${
                          checked
                            ? "bg-pb-green/10 border-pb-green/30 opacity-70"
                            : "bg-white border-pb-sky/30 hover:border-pb-coral/40 shadow-sm"
                        }`}
                      >
                        <span
                          className={`h-10 w-10 rounded-full flex items-center justify-center font-black text-lg flex-shrink-0 ${
                            checked
                              ? "bg-pb-green text-white"
                              : "bg-pb-coral text-white"
                          }`}
                        >
                          {checked ? "✓" : i + 1}
                        </span>
                        <p
                          className={`text-lg leading-relaxed pt-1 ${
                            checked ? "text-pb-soft line-through" : "text-pb-ink"
                          }`}
                        >
                          {s}
                        </p>
                      </li>
                    );
                  })}
                </ol>
              </section>
            )}

            {steps.length === 0 && entry.isCustom && (
              <p className="mt-6 p-4 rounded-2xl bg-pb-sun/20 border-2 border-dashed border-pb-sun text-pb-ink">
                A custom Buddy recipe — cook it any way your family loves! Use the ingredients above as your starting point.
              </p>
            )}

            {/* Callouts */}
            <div className="mt-8 space-y-3">
              {recipe.kid_task && (
                <div className="p-4 rounded-xl bg-pb-sky/15 border-l-4 border-pb-aqua">
                  <p className="text-xs font-extrabold uppercase tracking-widest text-pb-aqua">
                    Kid task
                  </p>
                  <p className="text-pb-ink text-sm mt-1">{recipe.kid_task}</p>
                </div>
              )}
              {recipe.plating_idea && (
                <div className="p-4 rounded-xl bg-pb-coral/10 border-l-4 border-pb-coral">
                  <p className="text-xs font-extrabold uppercase tracking-widest text-pb-coral">
                    Plating idea
                  </p>
                  <p className="text-pb-ink text-sm mt-1">{recipe.plating_idea}</p>
                </div>
              )}
              {recipe.family_tip && (
                <div className="p-4 rounded-xl bg-pb-green/10 border-l-4 border-pb-green">
                  <p className="text-xs font-extrabold uppercase tracking-widest text-pb-green">
                    Family tip
                  </p>
                  <p className="text-pb-ink text-sm mt-1">{recipe.family_tip}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Nav footer */}
        <div className="sticky bottom-0 bg-pb-ink/95 backdrop-blur px-5 sm:px-8 py-4 border-t border-white/10 flex items-center justify-between gap-3">
          <button
            data-testid="btn-cook-day-prev"
            onClick={() => setIdx((i) => Math.max(0, i - 1))}
            disabled={idx === 0}
            className="inline-flex items-center gap-2 rounded-full bg-white/10 text-white font-bold px-5 py-3 hover:bg-white/20 transition disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-4 w-4" /> Previous
          </button>
          <p className="text-white/60 text-xs font-bold hidden sm:block">
            Tap steps as you finish · Esc to exit
          </p>
          {idx < scheduled.length - 1 ? (
            <button
              data-testid="btn-cook-day-next"
              onClick={() => setIdx((i) => Math.min(scheduled.length - 1, i + 1))}
              className="inline-flex items-center gap-2 rounded-full bg-pb-coral text-white font-extrabold px-6 py-3 hover:scale-105 transition shadow-[0_12px_28px_rgba(242,106,52,0.35)]"
            >
              Next recipe <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              data-testid="btn-cook-day-done"
              onClick={onClose}
              className="inline-flex items-center gap-2 rounded-full bg-pb-green text-white font-extrabold px-6 py-3 hover:scale-105 transition shadow-[0_12px_28px_rgba(102,187,106,0.35)]"
            >
              Done cooking ✓
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
